/* AdminSectionShell — iter100.244.
 *
 * The shared "Analytics-style section card" used across the Admin
 * Command Center. Provides one visual grammar for every section:
 *   • Rounded outer surface (rounded-2xl border shadow-sm).
 *   • Gradient header strip with an UPPERCASE tinted eyebrow, a small
 *     muted description below, and an optional right slot for
 *     chips/links/actions.
 *   • Body area — children rendered as-is. Use `bodyPad` to opt into
 *     `p-5` padding, or leave it false to control layout yourself
 *     (e.g. hairline-divided KPI grids).
 *
 * Why: the Overview page previously had 3 competing header grammars
 *   (SectionHeader outside card, header-inside-card, and Analytics-
 *   style eyebrow). Now every section shares one shell so the page
 *   reads as one dashboard, not five.
 *
 * The `tone` prop controls the eyebrow colour + the gradient tint:
 *   primary | amber | rose | teal | violet | emerald
 * Tailwind classes are enumerated (not template-string interpolated)
 * so the JIT compiler picks them all up at build time.
 */
import React from "react";

const TONE_CLASSES = {
    primary: {
        eyebrow: "text-primary",
        gradient: "from-primary/5 via-card to-card",
    },
    amber: {
        eyebrow: "text-amber-700",
        gradient: "from-amber-50/60 via-card to-card",
    },
    rose: {
        eyebrow: "text-rose-700",
        gradient: "from-rose-50/60 via-card to-card",
    },
    teal: {
        eyebrow: "text-teal-700",
        gradient: "from-teal-50/60 via-card to-card",
    },
    violet: {
        eyebrow: "text-violet-700",
        gradient: "from-violet-50/60 via-card to-card",
    },
    emerald: {
        eyebrow: "text-emerald-700",
        gradient: "from-emerald-50/60 via-card to-card",
    },
};

export default function AdminSectionShell({
    eyebrow,
    tone = "primary",
    description,
    right,
    bodyPad = false,
    className = "",
    testid,
    children,
}) {
    const t = TONE_CLASSES[tone] || TONE_CLASSES.primary;
    return (
        <section
            className={`rounded-2xl border border-border bg-card shadow-sm overflow-hidden ${className}`}
            data-testid={testid}
        >
            <header className={`px-5 pt-4 pb-3 border-b border-border bg-gradient-to-r ${t.gradient}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className={`text-[10.5px] font-bold uppercase tracking-[0.18em] ${t.eyebrow}`}>
                            {eyebrow}
                        </div>
                        {description && (
                            <p className="text-[12.5px] text-muted-foreground mt-1 leading-snug max-w-2xl">
                                {description}
                            </p>
                        )}
                    </div>
                    {right && <div className="shrink-0">{right}</div>}
                </div>
            </header>
            <div className={bodyPad ? "p-5" : ""}>{children}</div>
        </section>
    );
}
